import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Table, Badge, Modal
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from 'react-dom';
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function NouvelleCommande() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [produits, setProduits] = useState([]);
    const [produitsAjoutes, setProduitsAjoutes] = useState([]);
    
    // Recherche fournisseur
    const [searchFournisseur, setSearchFournisseur] = useState('');
    const [showFournisseurResults, setShowFournisseurResults] = useState(false);
    const fournisseurInputRef = useRef(null);
    const [fournisseurPosition, setFournisseurPosition] = useState({ top: 0, left: 0, width: 0 });
    
    // Recherche produit
    const [searchProduit, setSearchProduit] = useState('');
    const [showProduitResults, setShowProduitResults] = useState(false);
    const produitInputRef = useRef(null);
    const [produitPosition, setProduitPosition] = useState({ top: 0, left: 0, width: 0 });
    
    const [formData, setFormData] = useState({
        id_fournisseur: '',
        fournisseur_nom: '',
        date_commande: new Date().toISOString().split('T')[0],
        date_livraison_prevue: '',
        conditions_reglement: '',
        lignes: []
    });

    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [produitSelectionne, setProduitSelectionne] = useState(null);
    const [quantite, setQuantite] = useState(1);
    const [prix, setPrix] = useState(0);
    const [remise, setRemise] = useState(0);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const produitId = params.get('produit');
        const quantiteParam = params.get('quantite');
        
        if (produitId) {
            sessionStorage.setItem('produit_auto', produitId);
            sessionStorage.setItem('quantite_auto', quantiteParam || '1');
        }
    }, [location.search]);

    useEffect(() => {
        const produitId = sessionStorage.getItem('produit_auto');
        if (produitId && produits.length > 0) {
            const produit = produits.find(p => p.id_produit === parseInt(produitId));
            const quantiteAuto = parseInt(sessionStorage.getItem('quantite_auto')) || 1;
            
            if (produit) {
                const nouvelleLigne = {
                    id_produit: produit.id_produit,
                    reference: produit.reference,
                    designation: produit.designation,
                    quantite_commandee: quantiteAuto,
                    prix_unitaire_ht: produit.prix_achat_ht || 0,
                    remise: 0,
                    tva_taux: produit.tva_taux || 0.00,
                    css_taux: produit.css_taux || 0.00,
                    montant_ht: quantiteAuto * (produit.prix_achat_ht || 0)
                };
                
                setFormData(prev => ({
                    ...prev,
                    lignes: [...prev.lignes, nouvelleLigne]
                }));
                
                setProduitsAjoutes([produit]);
                setTimeout(() => setProduitsAjoutes([]), 3000);
                
                sessionStorage.removeItem('produit_auto');
                sessionStorage.removeItem('quantite_auto');
            }
        }
    }, [produits]);

    useEffect(() => {
        if (location.state?.produits) {
            handleAjouterProduits(location.state.produits);
        }
    }, [location.state, produits]);

    const handleAjouterProduits = (produitsData) => {
        if (!produitsData || produitsData.length === 0 || produits.length === 0) return;

        const nouvellesLignes = produitsData.map(p => {
            const produitComplet = produits.find(prod => prod.id_produit === p.id);
            if (!produitComplet) return null;
            
            return {
                id_produit: produitComplet.id_produit,
                reference: produitComplet.reference,
                designation: produitComplet.designation,
                quantite_commandee: p.quantite || 1,
                prix_unitaire_ht: produitComplet.prix_achat_ht || 0,
                remise: 0,
                tva_taux: produitComplet.tva_taux ||0.00,
                css_taux: produitComplet.css_taux || 0.00,
                montant_ht: (p.quantite || 1) * (produitComplet.prix_achat_ht || 0)
            };
        }).filter(l => l !== null);

        if (nouvellesLignes.length > 0) {
            setFormData(prev => ({
                ...prev,
                lignes: [...prev.lignes, ...nouvellesLignes]
            }));
            
            setProduitsAjoutes(nouvellesLignes);
            setTimeout(() => setProduitsAjoutes([]), 3000);
        }
    };

    const updateFournisseurPosition = () => {
        if (fournisseurInputRef.current) {
            const rect = fournisseurInputRef.current.getBoundingClientRect();
            setFournisseurPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    const updateProduitPosition = () => {
        if (produitInputRef.current) {
            const rect = produitInputRef.current.getBoundingClientRect();
            setProduitPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (showFournisseurResults) updateFournisseurPosition();
            if (showProduitResults) updateProduitPosition();
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showFournisseurResults, showProduitResults]);

    const fetchFournisseurs = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/fournisseurs.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setFournisseurs(result.data || []);
        } catch (err) {
            console.error("Erreur chargement fournisseurs", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    useEffect(() => {
        fetchFournisseurs();
        fetchProduits();
    }, []);

    const handleFournisseurSelect = (fournisseur) => {
        setFormData({
            ...formData,
            id_fournisseur: fournisseur.id_fournisseur,
            fournisseur_nom: fournisseur.raison_sociale,
            conditions_reglement: fournisseur.conditions_reglement || ''
        });
        setSearchFournisseur(fournisseur.raison_sociale);
        setShowFournisseurResults(false);
    };

    const handleAjouterProduit = (produit) => {
        setProduitSelectionne(produit);
        setPrix(produit.prix_achat_ht || 0);
        setQuantite(1);
        setRemise(0);
        setShowProduitModal(true);
        setShowProduitResults(false);
    };

    const handleConfirmerProduit = () => {
        const nouvelleLigne = {
            id_produit: produitSelectionne.id_produit,
            reference: produitSelectionne.reference,
            designation: produitSelectionne.designation,
            quantite_commandee: quantite,
            prix_unitaire_ht: prix,
            remise: remise,
            tva_taux: produitSelectionne.tva_taux || 0.00,
            css_taux: produitSelectionne.css_taux || 0.00,
            montant_ht: quantite * prix * (1 - remise/100)
        };

        setFormData({
            ...formData,
            lignes: [...formData.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
        setSearchProduit('');
    };

    const handleSupprimerLigne = (index) => {
        const nouvellesLignes = formData.lignes.filter((_, i) => i !== index);
        setFormData({ ...formData, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        const montantHT = formData.lignes.reduce((acc, l) => acc + (l.montant_ht || 0), 0);
        const tva = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.tva_taux || 0) / 100), 0);
        const css = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.css_taux || 0) / 100), 0);
        return {
            montant_ht: montantHT,
            montant_tva: tva,
            montant_css: css,
            montant_ttc: montantHT + tva + css
        };
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.id_fournisseur) newErrors.fournisseur = "Veuillez sélectionner un fournisseur";
        if (formData.lignes.length === 0) newErrors.lignes = "Ajoutez au moins un produit";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitError('');

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const totaux = calculerTotaux();

            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    id_fournisseur: formData.id_fournisseur,
                    date_commande: formData.date_commande,
                    date_livraison_prevue: formData.date_livraison_prevue,
                    conditions_reglement: formData.conditions_reglement,
                    ...totaux,
                    lignes: formData.lignes,
                    id_utilisateur: user?.id_utilisateur || 1
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/achats/commandes');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const totaux = calculerTotaux();
    const fournisseursFiltres = fournisseurs.filter(f => 
        f.raison_sociale?.toLowerCase().includes(searchFournisseur.toLowerCase()) ||
        f.code_fournisseur?.toLowerCase().includes(searchFournisseur.toLowerCase())
    );

    const produitsFiltres = produits.filter(p => 
        p.designation?.toLowerCase().includes(searchProduit.toLowerCase()) ||
        p.reference?.toLowerCase().includes(searchProduit.toLowerCase())
    );

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-add-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Nouvelle commande</h4>
                                    <p className="text-muted small mb-0">Créer une commande fournisseur</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/achats/commandes')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {submitSuccess && (
                        <Alert variant="success" onClose={() => setSubmitSuccess(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Commande créée avec succès ! Redirection...
                        </Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger" onClose={() => setSubmitError('')} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {submitError}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col lg={8}>
                                {/* Sélection fournisseur */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-truck-line me-2"></i>
                                            Fournisseur
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Rechercher un fournisseur <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                ref={fournisseurInputRef}
                                                type="text"
                                                placeholder="Nom du fournisseur..."
                                                value={searchFournisseur}
                                                onChange={(e) => {
                                                    setSearchFournisseur(e.target.value);
                                                    setShowFournisseurResults(true);
                                                    setTimeout(updateFournisseurPosition, 10);
                                                }}
                                                onFocus={() => {
                                                    setShowFournisseurResults(true);
                                                    updateFournisseurPosition();
                                                }}
                                                onBlur={() => setTimeout(() => setShowFournisseurResults(false), 200)}
                                                isInvalid={!!errors.fournisseur}
                                                className="border-2 rounded-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.fournisseur}
                                            </Form.Control.Feedback>
                                            
                                            {showFournisseurResults && searchFournisseur && fournisseursFiltres.length > 0 && ReactDOM.createPortal(
                                                <div 
                                                    className="dropdown-results"
                                                    style={{
                                                        position: 'absolute',
                                                        top: fournisseurPosition.top,
                                                        left: fournisseurPosition.left,
                                                        width: fournisseurPosition.width,
                                                        zIndex: 99999,
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '12px',
                                                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto'
                                                    }}
                                                >
                                                    {fournisseursFiltres.map(f => (
                                                        <div
                                                            key={f.id_fournisseur}
                                                            className="p-3 border-bottom"
                                                            onClick={() => handleFournisseurSelect(f)}
                                                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                        >
                                                            <strong className="d-block">{f.raison_sociale}</strong>
                                                            <small className="text-muted">
                                                                {f.code_fournisseur} | {f.telephone || 'Tél non renseigné'}
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>,
                                                document.body
                                            )}
                                        </Form.Group>

                                        {formData.id_fournisseur && (
                                            <div className="selected-card mt-3 p-3 rounded-3" style={{ background: '#e2f3f5', borderLeft: `3px solid #21ACCD` }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="ri-checkbox-circle-line text-success"></i>
                                                            <span className="fw-semibold">{formData.fournisseur_nom}</span>
                                                            <Badge className="rounded-pill px-2" style={{ background: '#21ACCD' }}>
                                                                Sélectionné
                                                            </Badge>
                                                        </div>
                                                        {formData.conditions_reglement && (
                                                            <small className="text-muted d-block mt-1">
                                                                <i className="ri-file-text-line me-1"></i>
                                                                {formData.conditions_reglement}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <Button 
                                                        variant="link" 
                                                        className="text-muted p-0"
                                                        onClick={() => {
                                                            setFormData({
                                                                ...formData,
                                                                id_fournisseur: '',
                                                                fournisseur_nom: '',
                                                                conditions_reglement: ''
                                                            });
                                                            setSearchFournisseur('');
                                                        }}
                                                    >
                                                        <i className="ri-close-circle-line fs-5"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* Dates */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calendar-line me-2"></i>
                                            Dates
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date commande</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={formData.date_commande}
                                                        onChange={(e) => setFormData({...formData, date_commande: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date livraison prévue</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={formData.date_livraison_prevue}
                                                        onChange={(e) => setFormData({...formData, date_livraison_prevue: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Produits */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitResults(!showProduitResults)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {showProduitResults && (
                                            <div className="mb-4">
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-light border-2">
                                                        <i className="ri-search-line"></i>
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        ref={produitInputRef}
                                                        type="text"
                                                        placeholder="Rechercher un produit..."
                                                        value={searchProduit}
                                                        onChange={(e) => {
                                                            setSearchProduit(e.target.value);
                                                            setTimeout(updateProduitPosition, 10);
                                                        }}
                                                        onFocus={updateProduitPosition}
                                                        className="border-2"
                                                    />
                                                </InputGroup>
                                                
                                                {searchProduit && produitsFiltres.length > 0 && ReactDOM.createPortal(
                                                    <div 
                                                        className="dropdown-results"
                                                        style={{
                                                            position: 'absolute',
                                                            top: produitPosition.top,
                                                            left: produitPosition.left,
                                                            width: produitPosition.width,
                                                            zIndex: 99999,
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px',
                                                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto'
                                                        }}
                                                    >
                                                        {produitsFiltres.map(p => (
                                                            <div
                                                                key={p.id_produit}
                                                                className="p-3 border-bottom d-flex justify-content-between align-items-center"
                                                                onClick={() => handleAjouterProduit(p)}
                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                            >
                                                                <div>
                                                                    <strong className="d-block">{p.designation}</strong>
                                                                    <small className="text-muted">
                                                                        Réf: {p.reference} | Prix: {formatCurrency(p.prix_achat_ht)}
                                                                    </small>
                                                                </div>
                                                                <Badge className="rounded-pill px-3" style={{ background: '#21ACCD' }}>
                                                                    Ajouter
                                                                </Badge>
                                                            </div>
                                                        ))}
                                                    </div>,
                                                    document.body
                                                )}
                                            </div>
                                        )}

                                        {formData.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 products-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Produit</th>
                                                            <th>Réf.</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix unit.</th>
                                                            <th className="text-center">Remise</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.lignes.map((ligne, index) => (
                                                            <tr key={index} className="product-row">
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td><span className="reference-badge">{ligne.reference}</span></td>
                                                                <td className="text-center">
                                                                    <span className="quantity-badge">{ligne.quantite_commandee}</span>
                                                                </td>
                                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                                <td className="text-center">{ligne.remise}%</td>
                                                                <td className="text-end fw-bold text-primary">{formatCurrency(ligne.montant_ht)}</td>
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => handleSupprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={4}>
                                {/* Résumé */}
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Résumé
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.montant_ht)}</span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">TVA </span>
                                            <span className="text-warning fw-semibold">{formatCurrency(totaux.montant_tva)}</span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="text-muted">CSS </span>
                                            <span className="text-info fw-semibold">{formatCurrency(totaux.montant_css)}</span>
                                        </div>
                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-4">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-4">{formatCurrency(totaux.montant_ttc)}</span>
                                        </div>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Conditions de règlement</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={formData.conditions_reglement}
                                                onChange={(e) => setFormData({...formData, conditions_reglement: e.target.value})}
                                                placeholder="Ex: Paiement 30 jours..."
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                className="rounded-pill py-2"
                                                disabled={loading}
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-check-line me-2"></i>
                                                        Créer la commande
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/achats/commandes')}
                                                className="rounded-pill py-2"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>

                    {/* Modal d'ajout de produit */}
                    <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} centered className="modern-modal">
                        <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
                            <Modal.Title className="w-100">
                                <div className="text-center">
                                    <div className="confirm-icon mb-3">
                                        <i className="ri-shopping-cart-line text-white" style={{ fontSize: '4rem' }}></i>
                                    </div>
                                    <h3 className="fw-bold text-white">Ajouter {produitSelectionne?.designation}</h3>
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="pt-4">
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Quantité</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={quantite}
                                        onChange={(e) => setQuantite(parseInt(e.target.value) || 1)}
                                        className="border-2 rounded-3"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Prix unitaire HT</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={prix}
                                            onChange={(e) => setPrix(parseFloat(e.target.value) || 0)}
                                            className="border-2"
                                        />
                                        <InputGroup.Text className="border-2">FCFA</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Remise (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={remise}
                                        onChange={(e) => setRemise(parseFloat(e.target.value) || 0)}
                                        className="border-2 rounded-3"
                                    />
                                </Form.Group>
                                <hr />
                                <div className="d-flex justify-content-between recap-line">
                                    <span className="fw-semibold">Total HT</span>
                                    <strong className="text-primary fs-5">{formatCurrency(quantite * prix * (1 - remise/100))}</strong>
                                </div>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                            <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                                Annuler
                            </Button>
                            <Button variant="primary" onClick={handleConfirmerProduit} className="rounded-pill px-5" style={{ background: '#21ACCD', border: 'none' }}>
                                <i className="ri-add-line me-2"></i>
                                Ajouter
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                .quantity-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                
                .recap-line {
                    padding: 0.5rem 0;
                }
                
                .selected-card {
                    transition: all 0.2s ease;
                }
                
                .selected-card:hover {
                    transform: translateX(3px);
                }
                
                .dropdown-results {
                    animation: fadeIn 0.2s ease-out;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-16 {
                    font-size: 1rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}